import type { SchematicNode, SchematicPoint } from '@ghp/domain'
import { cloneElement, memo, type ReactElement } from 'react'

import { isTag, symbolExtent, tagBox, tagText } from './schematic-document'
import s from './schematic.module.css'

/**
 * Le rendu d'une forme, **en éléments SVG**.
 *
 * ⚠️ SVG et non `<canvas>`, et c'est le choix structurant de cet éditeur. Le test de collision,
 * la sélection, le déplacement et l'annulation sont quasi gratuits avec des éléments SVG et un
 * état React : le navigateur route l'événement vers la forme touchée, et un dessin se repeint
 * en remplaçant un tableau. En canvas impératif, tout cela est à écrire — y compris le test de
 * collision d'une polyligne, qui est exactement le genre de code qu'on n'éprouve jamais assez.
 * La signature, elle, reste en canvas : elle produit un bitmap, pas des formes manipulables —
 * ce n'est pas le même problème.
 *
 * ⚠️ **Une forme inconnue se dessine quand même**, en polyligne : le schéma d'une tablette plus
 * récente que l'app ne doit pas se relire amputé. Le document garde tout (M21), le rendu aussi.
 *
 * ⚠️ **Chaque forme est doublée d'une zone de saisie invisible**, et sans elle l'éditeur ne se
 * pilote pas au doigt. Le `pointer-events` par défaut d'un élément SVG est `visiblePainted` :
 * l'intérieur d'une forme `fill: none` — regard, tabouret, siphon — **n'est pas une cible**, et
 * il ne reste que l'anneau, large de 2 à 7 unités de document, soit un à trois pixels d'écran.
 * Toucher le milieu d'un regard ne le sélectionnait donc pas, et « Supprimer » restait grisé
 * sur une forme que l'agent croyait tenir.
 *
 * Le catalogue est celui de GHP DIGIT (rapport 02 de la rétro-ingénierie), tailles ramenées à
 * notre {@link GLYPH} : un tabouret de 41 px y devient 34, un tampon de 62 en fait 52, et les
 * petits ouvrages (27 px) prennent 28 pour rester lisibles sur la tablette.
 */
export interface SchematicShapeProps {
  node: SchematicNode
  selected: boolean
}

/** La taille d'un élément réseau posé d'une touche, en unités de document. */
const GLYPH = 34
/** Le rayon des petits ouvrages : gouttière, bouchon, bonde de sol, poste de relevage, siphons. */
const SMALL = 14
/** Le rayon du tampon, le plus grand des symboles. */
const COVER = 26
/** La largeur de la bande du caniveau à grille, et l'écart de ses barreaux. */
const CHANNEL = 22
const CHANNEL_BARS = 8

/**
 * ⚠️ **Mémoïsé, et ce n'est pas une précaution de style.** Le plan entier est reconstruit à
 * chaque `pointermove` — chaque forme, plus son doublon de capture, plus la sérialisation des
 * points en attribut `points`. Mesuré à 1,19 ms par déplacement du doigt sur un plan de
 * 200 formes, **sur un portable** ; une tablette est quatre à six fois plus lente, et le crayon
 * émet un événement par rafraîchissement d'écran. Les fonctions du document rendent les nœuds
 * intacts **par identité** (`mapNode` remplace un seul élément du tableau), donc la comparaison
 * par défaut suffit : seule la forme qui change se reconcilie.
 */
export const SchematicShape = memo(function SchematicShape({
  node,
  selected,
}: SchematicShapeProps) {
  const geometry = geometryOf(node)
  const capture = captureOf(geometry, node)
  const head = headOf(node)
  const tag = isTag(node)
  const anchor = labelAnchor(node)

  return (
    <g
      className={s.shape}
      data-node-id={node.id}
      data-stroke={node.stroke}
      data-width={node.strokeWidth}
      strokeWidth={STROKE_WIDTHS[node.strokeWidth]}
      data-selected={selected ? '' : undefined}
    >
      {capture}
      {geometry}
      {head}
      {tag && (
        <text className={s.tag} x={node.points[0]?.x ?? 0} y={node.points[0]?.y ?? 0}>
          {tagText(node)}
        </text>
      )}
      {!tag && node.text != null && node.text !== '' && (
        <text className={s.label} x={anchor.x} y={anchor.y}>
          {node.text}
        </text>
      )}
    </g>
  )
})

/**
 * La largeur de la zone de saisie, en unités de document — environ 12 px d'écran au repos sur
 * une tablette en paysage, donc de l'ordre d'une pulpe de doigt de part et d'autre du trait.
 */
const CAPTURE_WIDTH = 28

/**
 * Le doublon **invisible** qui reçoit le doigt : même géométrie, trait transparent et large,
 * posé **sous** la forme pour ne rien en masquer.
 *
 * ⚠️ Deux valeurs de `pointer-events`, et la distinction n'est pas cosmétique. Une forme fermée
 * — regard, tabouret, fosse, boîte — prend `all`, qui rend son **intérieur** touchable même sans
 * remplissage : c'est tout l'objet de ce doublon. Une conduite, elle, prend `stroke` : avec
 * `all`, le creux d'une polyligne en U deviendrait une cible et l'agent sélectionnerait une
 * conduite en touchant le vide à côté.
 *
 * ⚠️ **Un symbole composé ne se clone pas** — regard à grille, bonde, poste de relevage,
 * siphons, caniveau : ses traits intérieurs portent leur propre classe, et un clone les
 * redessinerait à l'épaisseur de capture. Il reçoit à la place un disque, ou la bande du
 * caniveau, de la taille du symbole.
 *
 * ⚠️ Les attributs de présentation portent la largeur et la couleur plutôt qu'une classe : le
 * groupe déclare `stroke-width` par jeton d'épaisseur, et une valeur héritée perd face à un
 * attribut posé sur l'élément lui-même — la classe, elle, demanderait un `!important`.
 */
function captureOf(geometry: ReactElement | null, node: SchematicNode): ReactElement | null {
  const [first, second] = node.points
  if (first === undefined) return null

  const tag = tagBox(node)
  if (tag !== null) {
    return (
      <rect data-capture="" {...tag} fill="transparent" stroke="transparent" pointerEvents="all" />
    )
  }

  if (node.kind === 'gratedChannel' && second !== undefined) {
    const { length, rotation } = segmentOf(first, second)
    return (
      <rect
        data-capture=""
        x={first.x - 8}
        y={first.y - CHANNEL / 2 - 8}
        width={length + 16}
        height={CHANNEL + 16}
        transform={rotation}
        fill="transparent"
        stroke="transparent"
        pointerEvents="all"
      />
    )
  }

  // La gouttière sur sol se prend par son rond comme par sa flèche : c'est la flèche que l'agent
  // voit, et le rond seul est un petit ouvrage.
  if (node.kind === 'downpipeOutfall' && second !== undefined) {
    return (
      <g data-capture="" fill="transparent" stroke="transparent" pointerEvents="all">
        <circle cx={first.x} cy={first.y} r={SMALL + 6} />
        <line
          x1={first.x}
          y1={first.y}
          x2={second.x}
          y2={second.y}
          strokeWidth={CAPTURE_WIDTH}
          pointerEvents="stroke"
        />
      </g>
    )
  }

  if (geometry === null) return null

  if (geometry.type === 'g') {
    return (
      <circle
        data-capture=""
        cx={first.x}
        cy={first.y}
        r={extentOf(node) + 6}
        fill="none"
        stroke="transparent"
        strokeWidth={CAPTURE_WIDTH}
        pointerEvents="all"
      />
    )
  }

  return cloneElement(geometry, {
    className: undefined,
    'data-capture': '',
    fill: 'none',
    stroke: 'transparent',
    strokeWidth: CAPTURE_WIDTH,
    pointerEvents: isClosed(node) ? 'all' : 'stroke',
  } as Partial<typeof geometry.props>)
}

/** Les formes dont l'intérieur est une surface, par opposition à un trait qui chemine. */
function isClosed(node: SchematicNode): boolean {
  return CLOSED_KINDS.has(node.kind)
}

const CLOSED_KINDS: ReadonlySet<string> = new Set([
  'manhole',
  'trap',
  'inspectionChamber',
  'septicTank',
  'rectangle',
  'ellipse',
  'text',
  'polygon',
  'manholeCover',
  'downpipe',
  'plug',
])

/** Le demi-côté du symbole, ou celui d'un regard pour ce qui n'en a pas. */
function extentOf(node: SchematicNode): number {
  return symbolExtent(node) || GLYPH / 2
}

/** Les cordes d'une grille ronde — la bonde de sol. */
function chords(center: SchematicPoint, radius: number, vertical: boolean): ReactElement[] {
  return [-8, -3, 3, 8].map((offset) => {
    const half = Math.sqrt(radius * radius - offset * offset)
    return vertical ? (
      <line
        key={`v${offset}`}
        x1={center.x + offset}
        y1={center.y - half}
        x2={center.x + offset}
        y2={center.y + half}
        strokeWidth={1.5}
      />
    ) : (
      <line
        key={`h${offset}`}
        x1={center.x - half}
        y1={center.y + offset}
        x2={center.x + half}
        y2={center.y + offset}
        strokeWidth={1.5}
      />
    )
  })
}

/** Les neuf pastilles du poste de relevage, en losange 1-2-3-2-1. */
const LIFTING_DOTS = [
  [0, -8],
  [-4, -4],
  [4, -4],
  [-8, 0],
  [0, 0],
  [8, 0],
  [-4, 4],
  [4, 4],
  [0, 8],
] as const

/** Le tiret de chaque réseau public : continu en EU, tireté en EP, tiret-point en unitaire. */
const MAIN_CLASS: Readonly<Record<string, string | undefined>> = {
  wastewater: s.stroked,
  stormwater: s.mainDashed,
  combined: s.mainDashDot,
}

function geometryOf(node: SchematicNode): ReactElement | null {
  const [first, second] = node.points
  if (first === undefined) return null

  switch (node.kind) {
    // ⚠️ Le Regard en carré, le Tabouret en cercle, comme GHP DIGIT (T14, T27, T41) : seuls les
    // dessins se sont échangés (#581), `manhole` reste le Regard du glossaire.
    case 'manhole':
      return (
        <rect
          className={s.hollow}
          x={first.x - GLYPH / 2}
          y={first.y - GLYPH / 2}
          width={GLYPH}
          height={GLYPH}
        />
      )
    case 'trap':
      return <circle className={s.hollow} cx={first.x} cy={first.y} r={GLYPH / 2} />
    case 'inspectionChamber':
      return <circle className={s.hollow} cx={first.x} cy={first.y} r={GLYPH / 2} />
    case 'septicTank':
      return (
        <rect
          className={s.filled}
          x={first.x - GLYPH}
          y={first.y - GLYPH / 2}
          width={GLYPH * 2}
          height={GLYPH}
        />
      )
    case 'manholeCover':
      return <circle className={s.paper} cx={first.x} cy={first.y} r={COVER} />
    case 'gratedInlet':
      return (
        <g className={s.hollow}>
          <rect x={first.x - GLYPH / 2} y={first.y - GLYPH / 2} width={GLYPH} height={GLYPH} />
          {[-10.2, -3.4, 3.4, 10.2].map((offset) => (
            <g key={offset}>
              <line
                x1={first.x + offset}
                y1={first.y - GLYPH / 2}
                x2={first.x + offset}
                y2={first.y + GLYPH / 2}
                strokeWidth={1.5}
              />
              <line
                x1={first.x - GLYPH / 2}
                y1={first.y + offset}
                x2={first.x + GLYPH / 2}
                y2={first.y + offset}
                strokeWidth={1.5}
              />
            </g>
          ))}
        </g>
      )
    case 'floorDrain':
      return (
        <g className={s.hollow}>
          <circle cx={first.x} cy={first.y} r={SMALL} strokeWidth={2} />
          {chords(first, SMALL, false)}
          {chords(first, SMALL, true)}
        </g>
      )
    case 'liftingStation':
      return (
        <g>
          <circle className={s.hollow} cx={first.x} cy={first.y} r={SMALL} strokeWidth={2} />
          {LIFTING_DOTS.map(([dx, dy]) => (
            <circle
              key={`${dx}:${dy}`}
              className={s.solid}
              cx={first.x + dx}
              cy={first.y + dy}
              r={2.2}
              strokeWidth={0}
            />
          ))}
        </g>
      )
    case 'downpipe':
      return <circle className={s.paper} cx={first.x} cy={first.y} r={SMALL} strokeWidth={2} />
    case 'downpipeOutfall':
      return second === undefined ? null : outfallOf(first, second, node)
    case 'plug':
      return <circle className={s.solid} cx={first.x} cy={first.y} r={SMALL} strokeWidth={0} />
    case 'trapHorizontal':
    case 'trapVertical': {
      const vertical = node.kind === 'trapVertical'
      return (
        <g className={s.solid} strokeWidth={0}>
          <circle cx={first.x - (vertical ? 0 : 9)} cy={first.y - (vertical ? 9 : 0)} r={SMALL} />
          <circle cx={first.x + (vertical ? 0 : 9)} cy={first.y + (vertical ? 9 : 0)} r={SMALL} />
        </g>
      )
    }
    case 'wastewaterPipe':
      return <polyline className={s.stroked} points={pathOf(node.points)} />
    case 'stormwaterPipe':
      return <polyline className={s.dashed} points={pathOf(node.points)} />
    case 'publicNetwork':
      return (
        <polyline className={MAIN_CLASS[node.stroke] ?? s.stroked} points={pathOf(node.points)} />
      )
    case 'gratedChannel':
      return second === undefined ? null : channelOf(first, second)
    case 'polygon':
      return <polygon className={s.hollow} points={pathOf(node.points)} />
    case 'shelter':
      return <polyline className={s.dashed} points={pathOf(node.points)} />
    case 'roomLabel':
    case 'outletLabel':
      return null
    case 'rectangle':
      return second === undefined ? null : <rect className={s.hollow} {...boxOf(first, second)} />
    case 'ellipse':
      return second === undefined ? null : (
        <ellipse
          className={s.hollow}
          cx={(first.x + second.x) / 2}
          cy={(first.y + second.y) / 2}
          rx={Math.abs(second.x - first.x) / 2}
          ry={Math.abs(second.y - first.y) / 2}
        />
      )
    case 'arrow':
      return <polyline className={s.stroked} points={pathOf(node.points)} />
    case 'text':
      // Le libellé est rendu au-dessus ; le point d'ancrage reste touchable, sinon une
      // annotation qu'on vient de poser ne se sélectionne plus pour être écrite.
      return <circle className={s.anchor} cx={first.x} cy={first.y} r={GLYPH / 3} />
    default:
      // `line`, `freehand` — et tout ce que cette version ne connaît pas.
      return <polyline className={s.stroked} points={pathOf(node.points)} />
  }
}

/**
 * L'épaisseur de chaque jeton, en unités de document. Portée par le groupe, parce que la pointe
 * en a besoin pour sa taille : une règle CSS ne se lit pas depuis le rendu.
 */
const STROKE_WIDTHS: Readonly<Record<string, number | undefined>> = {
  thin: 2,
  medium: 4,
  thick: 7,
}

/** Les formes qui portent une pointe au point aval — on trace de l'amont vers l'aval. */
const HEADS: Readonly<Record<string, 'arrow' | 'chevron' | undefined>> = {
  arrow: 'arrow',
  publicNetwork: 'arrow',
  wastewaterPipe: 'chevron',
  stormwaterPipe: 'chevron',
}

/**
 * La pointe, en V sur une conduite privée, pleine sur le réseau public et la flèche (GHP DIGIT).
 *
 * ⚠️ **Dessinée dans la forme, pas en `<marker>`.** Un marqueur vit dans `<defs>`, hors du groupe
 * qui porte la couleur, et se désigne par un identifiant : il en fallait un par couleur (#245).
 * En preview le 30/09/2026, la pointe gardait pourtant l'ancienne couleur, dans la palette comme
 * après un changement de réseau, alors que Chromium la repeignait : le défaut tenait au
 * navigateur. Ici elle hérite de la couleur du groupe comme le trait, et rien ne la désigne.
 */
function headOf(node: SchematicNode): ReactElement | null {
  const kind = HEADS[node.kind]
  const tip = node.points.at(-1)
  const from = node.points.findLast((point) => point.x !== tip?.x || point.y !== tip.y)
  if (kind === undefined || tip === undefined || from === undefined) return null

  const width = STROKE_WIDTHS[node.strokeWidth] ?? 4
  const angle = (Math.atan2(tip.y - from.y, tip.x - from.x) * 180) / Math.PI
  const place = `translate(${tip.x} ${tip.y}) rotate(${angle})`

  return kind === 'arrow' ? (
    <path
      className={s.solid}
      d="M -9 -5 L 1 0 L -9 5 z"
      transform={`${place} scale(${0.6 * width})`}
      strokeWidth={0}
    />
  ) : (
    <path
      className={s.stroked}
      d="M -7 -4 L 0 0 L -7 4"
      transform={`${place} scale(${0.5 * width})`}
      strokeWidth={2}
    />
  )
}

function pathOf(points: SchematicPoint[]): string {
  return points.map((point) => `${point.x},${point.y}`).join(' ')
}

/** La longueur d'un segment, et la rotation qui couche l'horizontale sur lui, autour de son début. */
function segmentOf(first: SchematicPoint, second: SchematicPoint) {
  const angle = (Math.atan2(second.y - first.y, second.x - first.x) * 180) / Math.PI
  return {
    length: Math.hypot(second.x - first.x, second.y - first.y),
    angle,
    rotation: `rotate(${angle} ${first.x} ${first.y})`,
  }
}

/**
 * Le caniveau à grille, **le long du segment tiré entre ses deux points**, dans n'importe quelle
 * direction (#581) : la bande de 22 centrée sur le segment, un barreau tous les 8. Il était
 * couché ou debout selon l'axe dominant du geste (#534) ; dessiné couché, puis tourné, il se fait
 * tourner en Sélection en tirant une extrémité, comme une conduite.
 */
function channelOf(first: SchematicPoint, second: SchematicPoint): ReactElement {
  const { length, rotation } = segmentOf(first, second)
  const bars: ReactElement[] = []
  for (let at = CHANNEL_BARS; at < length; at += CHANNEL_BARS) {
    bars.push(
      <line
        key={at}
        x1={first.x + at}
        y1={first.y - CHANNEL / 2}
        x2={first.x + at}
        y2={first.y + CHANNEL / 2}
        strokeWidth={1.5}
      />,
    )
  }
  return (
    <g className={s.hollow} transform={rotation}>
      <rect x={first.x} y={first.y - CHANNEL / 2} width={length} height={CHANNEL} strokeWidth={2} />
      {bars}
    </g>
  )
}

/**
 * La gouttière sur sol — la « Gouttière Écoulement » de GHP DIGIT (#581) : le rond de la
 * gouttière, puis la flèche du rejet, continue et à pointe pleine, comme l'outil Flèche. C'est de
 * l'eau qui coule au sol, pas une conduite EP : pas de tireté.
 *
 * ⚠️ Une flèche qui tiendrait dans le rond ne se dessine pas : elle n'y montrerait aucun sens.
 */
function outfallOf(
  first: SchematicPoint,
  second: SchematicPoint,
  node: SchematicNode,
): ReactElement {
  const pipe = <circle className={s.paper} cx={first.x} cy={first.y} r={SMALL} strokeWidth={2} />
  const { length, angle } = segmentOf(first, second)
  if (length <= SMALL) return pipe

  const edge = {
    x: first.x + ((second.x - first.x) * SMALL) / length,
    y: first.y + ((second.y - first.y) * SMALL) / length,
  }
  const width = STROKE_WIDTHS[node.strokeWidth] ?? 4
  return (
    <g>
      <line className={s.stroked} x1={edge.x} y1={edge.y} x2={second.x} y2={second.y} />
      <path
        className={s.solid}
        d="M -9 -5 L 1 0 L -9 5 z"
        transform={`translate(${second.x} ${second.y}) rotate(${angle}) scale(${0.6 * width})`}
        strokeWidth={0}
      />
      {pipe}
    </g>
  )
}

function boxOf(from: SchematicPoint, to: SchematicPoint) {
  return {
    x: Math.min(from.x, to.x),
    y: Math.min(from.y, to.y),
    width: Math.abs(to.x - from.x),
    height: Math.abs(to.y - from.y),
  }
}

/**
 * Le libellé se pose au-dessus à droite du premier point — l'« EU » des conduites de la
 * maquette —, au-delà du bord d'un symbole posé d'un appui.
 */
function labelAnchor(node: SchematicNode): SchematicPoint {
  const first = node.points[0] ?? { x: 0, y: 0 }
  const offset = node.points.length === 1 ? extentOf(node) : GLYPH / 2

  return { x: first.x + offset, y: first.y - offset }
}
